import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame, useInView } from 'framer-motion';
import useElementSize from '@/hooks/use-element-size';
import { mergeRefs } from '@/utils/merge-refs';
import { viewport } from '@/utils/viewport';
import { Star } from './Star';
import { lerp } from '@/utils/lerp';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';
import { useAppReadyState } from '@/atoms/app-ready';

type Props = React.HTMLAttributes<HTMLElement>;

const AnimatedBackground = ({ ...props }: Props) => {
    const ref = useRef<HTMLCanvasElement | null>(null);
    const [dpr, setDpr] = useState(1);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
    const [appReady] = useAppReadyState();
    const inView = useInView(ref, { margin: '-50px 0px -50px 0px' });
    const [mediaQueryDevice] = useMediaQueryDeviceState();
    const [canvasRef, size] = useElementSize<HTMLCanvasElement>();

    const width = size.width * dpr;
    const height = size.height * dpr;

    const pointerX = useRef(viewport.width / 2);
    const pointerY = useRef(viewport.height / 2);
    const starLerpedPointer = useRef({ x: pointerX.current, y: pointerY.current });

    const isMobile = mediaQueryDevice === 'vertical-mobile' || mediaQueryDevice === 'vertical-tablet';

    const stars = useMemo(() => {
        const STARS_AMOUNT = mediaQueryDevice === 'desktop' ? 500 : 1000;
        const arr: Star[] = [];
        for (let i = 0; i < STARS_AMOUNT; i++) {
            const x = Math.round(Math.random() * width);
            const y = Math.round(Math.random() * height);
            const length = 1 + Math.random() * 2;
            const opacity = Math.random();
            const star = new Star(x, y, length, opacity);
            arr.push(star);
        }
        return arr;
    }, [width, height, mediaQueryDevice]);

    const centerX = width / 2;

    useEffect(() => {
        if (ref.current) setCtx(ref.current.getContext('2d'));
    }, [ctx]);

    useEffect(() => {
        const onResize = () => setDpr(Math.min(window.devicePixelRatio, 2));
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const onMousemove = (event: MouseEvent) => {
            if (!isMobile) {
                pointerX.current = event.clientX;
                pointerY.current = event.clientY;
            }
        };

        if (inView && !isMobile) {
            document.addEventListener('mousemove', onMousemove);
        }

        return () => document.removeEventListener('mousemove', onMousemove);
    }, [inView, isMobile]);

    useAnimationFrame(() => {
        if (appReady && ctx && inView) {
            ctx.clearRect(0, 0, width, height);

            // На мобильных устройствах звезды остаются по центру
            if (!isMobile) {
                starLerpedPointer.current.x = lerp(starLerpedPointer.current.x, pointerX.current, 0.05);
            }

            const starOffset = isMobile ? 0 : (starLerpedPointer.current.x * dpr - centerX) * 0.05;

            for (let i = 0; i < stars.length; i++) {
                stars[i].draw(ctx, starOffset, width, height);
            }
        }
    });

    return (
        <div {...props} className={classNames('section__canvas', props.className)}>
            <canvas ref={mergeRefs([ref, canvasRef])} className="index__canvas-el" width={width} height={height} />
        </div>
    );
};

export default AnimatedBackground;
